import { gql } from '@apollo/client';

export const GET_CREDIT_FACILITY = gql`
  query GetCreditFacility($creditFacilityId: UUID!) {
    creditFacility(creditFacilityId: $creditFacilityId) {
      id
      slug
      isActive
      borrowerId
      name
      type
      implementationId
      externalFacilityId
      capitalProviderRepresentativeOrganization {
        id
        name
      }
      loanProducts {
        id
        name
        slug
        implementationId
      }
    }
  }
`;

export const GET_CREDIT_FACILITY_CAPITAL_PROVIDER_REPRESENTATIVE = gql`
  query GetCreditFacilityCapitalProviderRepresentative(
    $creditFacilityId: UUID!
  ) {
    creditFacility(creditFacilityId: $creditFacilityId) {
      capitalProviderRepresentativeOrganization {
        id
        slug
        name
      }
    }
  }
`;

export const GET_CREDIT_FACILITY_BORROWER_ENTITY_NAME = gql`
  query GetCreditFacilityBorrowerEntityName($creditFacilityId: UUID!) {
    creditFacility(creditFacilityId: $creditFacilityId) {
      borrowerEntityName
    }
  }
`;

export const GET_CREDIT_FACILITY_EXTERNAL_FACILITY_ID = gql`
  query GetCreditFacilityExternalFacilityId($creditFacilityId: UUID!) {
    creditFacility(creditFacilityId: $creditFacilityId) {
      externalFacilityId
    }
  }
`;

export const GET_MULTIPLE_EXTERNAL_FACILITY_IDS = gql`
  query GetMultipleExternalFacilityIds($creditFacilityIds: [UUID!]!) {
    result: getMultipleExternalFacilityIds(
      creditFacilityIds: $creditFacilityIds
    ) {
      creditFacilityId
      externalFacilityId
    }
  }
`;

export const CHECK_IF_ANY_FACILITIES_HAVE_BORROWING_BASE = gql`
  query CheckIfAnyFacilitiesHaveBorrowingBase(
    $creditFacilityIds: [UUID!]!
    $creditFacilitySlugsToHide: [String!]
    $useBatchOptimization: Boolean
  ) {
    hasBorrowingBase: checkIfAnyFacilitiesHaveBorrowingBase(
      creditFacilityIds: $creditFacilityIds
      creditFacilitySlugsToHide: $creditFacilitySlugsToHide
      useBatchOptimization: $useBatchOptimization
    )
  }
`;

export const GET_LOAN_PRODUCTS_AND_ASSOCIATED_FACILITIES_FOR_CREDIT_FACILITY = gql`
  query GetLoanProductsAndAssociatedFacilitiesForCreditFacility(
    $creditFacilityId: UUID!
  ) {
    creditFacility(creditFacilityId: $creditFacilityId) {
      id
      slug
      name
      implementationId
      loanProducts {
        id
        name
        slug
        currentCreditFacilities {
          id
          slug
          name
        }
      }
    }
  }
`;

export const GET_CREDIT_FACILITY_WITH_CURRENT_CREDIT_AGREEMENT = gql`
  query GetCreditFacilityWithCurrentCreditAgreement($creditFacilityId: UUID!) {
    creditFacility(creditFacilityId: $creditFacilityId) {
      id
      slug
      isActive
      borrowerId
      name
      type
      loanProducts {
        id
        name
        slug
      }
      currentCreditAgreement {
        id
        creditAgreement
      }
    }
  }
`;

export const GET_CREDIT_FACILITIES = gql`
  query GetCreditFacilities($organizationId: UUID!) {
    creditFacilities(organizationId: $organizationId) {
      id
      slug
      name
      isActive
      borrowerId
      borrowerEntityName
      type
      implementationId
    }
  }
`;

export const GET_CREDIT_FACILITIES_FOR_ASSIGNMENT_GROUPING = gql`
  query GetCreditFacilitiesForAssignmentGrouping($implementationId: UUID!) {
    creditFacilitiesForAssignmentGrouping(implementationId: $implementationId) {
      id
      slug
      name
      isActive
      borrowerId
      borrowerEntityName
      type
    }
  }
`;

export const GET_CREDIT_FACILITIES_AND_LOAN_PRODUCTS_FOR_ASSIGNMENT_GROUPING = gql`
  query GetCreditFacilitiesAndLoanProductsForAssignmentGrouping(
    $implementationId: UUID!
  ) {
    creditFacilitiesForAssignmentGrouping(implementationId: $implementationId) {
      id
      slug
      name
      loanProducts {
        id
        name
      }
    }
  }
`;

export const GET_CREDIT_FACILITIES_AND_LOAN_PRODUCTS_WITH_BORROWING_BASE_MECHANICS_FOR_ASSIGNMENT_GROUPING = gql`
  query GetCreditFacilitiesAndLoanProductsWithBorrowingBaseMechanicsForAssignmentGrouping(
    $implementationId: UUID!
  ) {
    creditFacilitiesForAssignmentGrouping(implementationId: $implementationId) {
      id
      name
      slug
      implementationId
      borrowingBaseMechanicsForLoanProducts {
        loanProductId
        loanProductName
        implementationId
        baseFields
        eligibilityCriteria {
          eligibilityCriteriaKey
          eligibilityConfigDetail
        }
      }
    }
  }
`;

export const GET_CREDIT_FACILITY_LIABILITY_ACCOUNTS = gql`
  query GetCreditFacilityLiabilityAccounts($creditFacilityId: UUID!) {
    creditFacility(creditFacilityId: $creditFacilityId) {
      id
      liabilityAccounts {
        tAccount
        organization {
          id
          name
        }
      }
    }
  }
`;

// TODO: @haghanim eventually update underlying controller to make more performantive call - https://linear.app/finleycms/issue/ENG-10838/build-controller-to-directly-call
// TODO: when we select new CreditFacilities and Capital Providers, we must refetch liability accounts data too. @haghanim create a new backend call - https://linear.app/finleycms/issue/ENG-10936/when-creditfacilities-and-capital-providers-narrow-down-we-need-to
export const GET_LIABILITY_ACCOUNTS_OF_CREDIT_FACILITIES = gql`
  query GetCreditFacilitiesLiabilityAccounts($organizationId: UUID!) {
    creditFacilities(organizationId: $organizationId) {
      id
      liabilityAccounts {
        tAccount
      }
    }
  }
`;

export const UPDATE_CREDIT_FACILITY_IS_ACTIVE = gql`
  mutation UpdateCreditFacilityIsActive(
    $creditFacilityId: UUID!
    $isActive: Boolean!
  ) {
    updateCreditFacilityIsActive(
      creditFacilityId: $creditFacilityId
      isActive: $isActive
    ) {
      response
    }
  }
`;

export const UPDATE_CREDIT_FACILITY_NAME = gql`
  mutation UpdateCreditFacilityName($creditFacilityId: UUID!, $name: String!) {
    updateCreditFacilityName(creditFacilityId: $creditFacilityId, name: $name) {
      id
      name
    }
  }
`;

export const GET_ALL_DOCUMENTS_UPLOADED = gql`
  query getUploadedDocuments($creditFacilityId: UUID!) {
    creditFacility(creditFacilityId: $creditFacilityId) {
      uploadedDocuments {
        id
        documentType
        capitalProvider {
          id
          name
        }
        documentName
        status
        uploadedAt
      }
    }
  }
`;

export const GET_CREDIT_FACILITY_KNOWLEDGE_BASE_DOCUMENT_ID = gql`
  query getCreditFacilityKnowledgeBaseDocumentId($creditFacilityId: UUID!) {
    creditFacility(creditFacilityId: $creditFacilityId) {
      knowledgeBaseDocumentId
    }
  }
`;

export const GET_DOCUMENT_UPLOAD_SIGNED_URL = gql`
  query getDocumentUploadSignedUrl($serializedParams: String!) {
    getSignedUploadUrl(
      feature: "getDocumentUploadDestination"
      serializedParams: $serializedParams
    ) {
      url
      s3RequestFields
      queryResponseFields
    }
  }
`;

export const GET_DOCUMENT_DOWNLOAD_SIGNED_URL = gql`
  query getDocumentDownloadDestination($serializedParams: String!) {
    downloadUrls: getSignedDownloadUrls(
      feature: "getDocumentDownloadDestination"
      serializedParams: $serializedParams
    ) {
      url
      filename
    }
  }
`;

export const UPDATE_DOCUMENT_TO_LATEST_PUBLISHED = gql`
  mutation updateDocumentToLatestPublished($documentId: UUID!) {
    updateDocumentToLatestPublished(documentId: $documentId) {
      response
    }
  }
`;

export const UPDATE_DOCUMENT_NAME = gql`
  mutation updateDocumentName($documentId: UUID!, $newDocumentName: String!) {
    updateDocumentName(
      documentId: $documentId
      newDocumentName: $newDocumentName
    ) {
      response
    }
  }
`;

export const UPLOAD_CA_TO_KNOWLEDGE_BASE = gql`
  mutation uploadCreditAgreementToKnowledgeBase(
    $creditFacilityDocumentId: UUID!
  ) {
    uploadCreditAgreementToKnowledgeBase(
      creditFacilityDocumentId: $creditFacilityDocumentId
    ) {
      response
    }
  }
`;

export const CREATE_CREDIT_FACILITY = gql`
  mutation CreateCreditFacility(
    $slug: String!
    $name: String!
    $implementationId: UUID!
    $capitalProviderIds: [UUID!]!
    $capitalProviderRepresentativeIds: [UUID!]!
    $type: FacilityType!
  ) {
    createCreditFacility(
      slug: $slug
      name: $name
      implementationId: $implementationId
      capitalProviderIds: $capitalProviderIds
      capitalProviderRepresentativeIds: $capitalProviderRepresentativeIds
      type: $type
    ) {
      id
    }
  }
`;
